import { useCallback } from 'react'

import { useQueryClient } from 'react-query'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { expenseQueryConfig } from '@webapp/modules/expense/query-configs/expense'
import messages from './messages'
import { useMutateSaveExpenseDraft } from '../../models/useMutateSaveExpenseDraft'

interface UseSaveExpenseDraftPresenterParams {
  onSuccess: (draftId: number) => void
}

export const useSaveExpenseDraftPresenter = ({
  onSuccess,
}: UseSaveExpenseDraftPresenterParams) => {
  const intl = useProcurifyIntl()
  const queryClient = useQueryClient()
  const { mutateAsync, isLoading: isMutating } = useMutateSaveExpenseDraft()

  const saveDraft = useCallback(
    async (formValues: ExpenseReportFormSchema) => {
      try {
        const result = await mutateAsync(formValues)

        queryClient.invalidateQueries(
          expenseQueryConfig.requestFormData().queryKey
        )

        onSuccess(result.id)
      } catch (err) {
        const errorMessage =
          getMessageFromError(err) ?? intl.formatMessage(messages.genericError)

        toast.error({
          title: intl.formatMessage(messages.genericErrorTitle),
          children: errorMessage,
        })
      }
    },
    [intl, mutateAsync, onSuccess]
  )

  return {
    commands: {
      saveDraft,
    },
    states: {
      isLoading: isMutating,
    },
  }
}
